<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import { ThreadsApi, Configuration, type SendOptionsResponse } from "@/api/javaapi";
import { useAuthStore } from "@/stores/auth.store";
import { useToast } from "primevue/usetoast";
import { useRouter } from "vue-router";
import { debounce } from "@/helpers/debounce.ts";

const props = defineProps<{
  initialReservationNumber?: string;
}>();

const emit = defineEmits<{
  (e: "thread-created", threadId: number): void;
}>();

const router = useRouter();
const authStore = useAuthStore();
const toast = useToast();

const threadApi = new ThreadsApi(new Configuration({ accessToken: authStore.token ?? undefined }));

const phoneInput = ref<string>("");
const reservationNumber = ref<string | null>(null);
const selectedPhone = ref<string>("");
const channelOptions = ref<{ label: string; value: string; threadId: number | null }[]>([
  { label: "Phone", value: "TEXT", threadId: null },
]);
const selectedChannel = ref<string>("TEXT");
const phoneNumberOptions = ref<{ label: string; value: string; threadId: number | null }[]>([]);
const loading = ref<boolean>(false);
const inputMode = ref<'phone' | 'reservation' | null>(null);
const reservationNotFound = ref<boolean>(false);

const reservationNumberAsNumber = computed({
  get: () => (reservationNumber.value ? Number(reservationNumber.value) : null),
  set: (value: number | null) => {
    reservationNumber.value = value != null ? String(value) : null;
  },
});

const debouncedGetSendOptions = debounce(async () => {
  if (inputMode.value === "phone" && !phoneInput.value) return;
  if (inputMode.value === "reservation" && !reservationNumberAsNumber.value) return;
  
  await getSendOptions();
}, 500);

const isPhoneInput = computed(() => phoneInput.value.trim().length > 0);
const isReservationInput = computed(() => reservationNumber.value !== null);
const isFormValid = computed(() => 
  (inputMode.value === "phone" && isPhoneInput.value) || 
  (inputMode.value === "reservation" && !reservationNotFound.value && (selectedChannel.value === "TEXT" ? !!selectedPhone.value : true))
);
const showChannelSelect = computed(() => 
  inputMode.value === "reservation" && channelOptions.value.length > 1
);
const showPhoneInput = computed(() => 
  inputMode.value === null || inputMode.value === "phone"
);
const noSendOptions = computed(() => 
  inputMode.value === "reservation" && 
  !reservationNotFound.value &&
  channelOptions.value.length === 0 && 
  phoneNumberOptions.value.length === 0
);

const getSendOptions = async () => {
  if (!reservationNumberAsNumber.value) {
    channelOptions.value = [{ label: "Phone", value: "TEXT", threadId: null }];
    selectedChannel.value = "TEXT";
    phoneNumberOptions.value = [];
    selectedPhone.value = "";
    reservationNotFound.value = false;
    return;
  }

  try {
    loading.value = true;
    const response = await threadApi.getSendOptions(reservationNumberAsNumber.value);
    const options = response.data;

    reservationNotFound.value = false;
    channelOptions.value = [];
    if ((options.phoneNumbers ?? []).length > 0) {
      channelOptions.value.push({ label: "Phone", value: "TEXT", threadId: null });
    }
    Object.entries(options.channelThreads ?? {}).forEach(([channel, threadId]) => {
      channelOptions.value.push({
        label: channel === "AIRBNB" ? "Airbnb" : channel === "BDC" ? "Booking.com" : "Vrbo",
        value: channel,
        threadId: threadId,
      });
    });
    selectedChannel.value = channelOptions.value[0]?.value ?? "TEXT";

    phoneNumberOptions.value = (options.phoneNumbers ?? [])
      .filter((p) => p.number !== undefined)
      .map((p) => ({
        label: `${p.number} (${p.description || p.type})`,
        value: p.number as string,
        threadId: p.threadId ?? null,
      }));
    selectedPhone.value = phoneNumberOptions.value[0]?.value ?? "";
  } catch (error) {
    console.error("Failed to get send options:", error);
    toast.add({ severity: "warn", summary: "Not Found", detail: "Reservation not found", life: 3000 });
    channelOptions.value = [];
    phoneNumberOptions.value = [];
    selectedPhone.value = "";
    reservationNotFound.value = true;
  } finally {
    loading.value = false;
  }
};

const createThread = async () => {
  try {
    loading.value = true;

    if (reservationNumberAsNumber.value) {
      if (selectedChannel.value === "TEXT") {
        const selectedPhoneOption = phoneNumberOptions.value.find((p) => p.value === selectedPhone.value);
        if (selectedPhoneOption?.threadId) {
          emit("thread-created", selectedPhoneOption.threadId);
          router.push(`/inbox/${selectedPhoneOption.threadId}`);
          return;
        }
      } else {
        const channelOption = channelOptions.value.find((c) => c.value === selectedChannel.value);
        if (channelOption?.threadId) {
          emit("thread-created", channelOption.threadId);
          router.push(`/inbox/${channelOption.threadId}`);
          return;
        }
      }
    }

    const request = {
      phoneNumber: inputMode.value === "phone" ? phoneInput.value : selectedPhone.value,
      reservationNumber: reservationNumberAsNumber.value ?? undefined,
      channel: selectedChannel.value,
    };
    const response = await threadApi.createNewThread(request);
    emit("thread-created", response.data.ID!);
    router.push(`/inbox/${response.data.ID!}`);
    toast.add({ severity: "success", summary: "Thread Created", detail: "New message thread created", life: 3000 });
  } catch (error) {
    console.error("Failed to create thread:", error);
    toast.add({ severity: "error", summary: "Error", detail: (error as Error).message || "Failed to create thread", life: 3000 });
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  if (props.initialReservationNumber) {
    reservationNumber.value = props.initialReservationNumber;
    inputMode.value = "reservation";
    phoneInput.value = "";
    debouncedGetSendOptions();
  }
});

watch([phoneInput, reservationNumber], ([newPhone, newRes]) => {
  if (newPhone && !newRes) {
    inputMode.value = "phone";
    reservationNumber.value = null;
    selectedPhone.value = "";
    reservationNotFound.value = false;
    debouncedGetSendOptions();
  } else if (newRes) {
    inputMode.value = "reservation";
    phoneInput.value = "";
    debouncedGetSendOptions();
  } else if (!newPhone && !newRes) {
    inputMode.value = null;
    selectedPhone.value = "";
    phoneNumberOptions.value = [];
    channelOptions.value = [{ label: "Phone", value: "TEXT", threadId: null }];
    selectedChannel.value = "TEXT";
    reservationNotFound.value = false;
  }
}, { immediate: false });
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="inputMode != 'reservation'">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
      <InputText
        v-model="phoneInput"
        class="w-full"
        placeholder="Enter phone number"
        :disabled="loading"
      />
    </div>
    <div v-if="inputMode !== 'phone'">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Reservation Number</label>
      <InputText
        v-model="reservationNumber"
        type="number"
        class="w-full"
        placeholder="Enter reservation number"
      />
    </div>
    <div v-if="showChannelSelect">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Send Via</label>
      <Dropdown
        v-model="selectedChannel"
        :options="channelOptions"
        optionLabel="label"
        optionValue="value"
        class="w-full"
        :disabled="loading"
      />
    </div>
    <div v-if="selectedChannel === 'TEXT' && inputMode === 'reservation' && phoneNumberOptions.length > 0">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
      <Dropdown
        v-model="selectedPhone"
        :options="phoneNumberOptions"
        optionLabel="label"
        optionValue="value"
        class="w-full"
        :disabled="loading"
      />
    </div>
    <div v-if="reservationNotFound" class="text-sm text-red-600 dark:text-red-400">
      Reservation not found
    </div>
    <div v-else-if="noSendOptions" class="text-sm text-red-600 dark:text-red-400">
      No messaging options available for this reservation
    </div>
    <Button label="Create" class="p-button-primary" :disabled="!isFormValid || loading" :loading="loading" @click="createThread" />
  </div>
</template>